/**
 * Day.js configuration and setup
 * Replaces moment.js with day.js for date/time operations
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/es';
import 'dayjs/locale/it';
import 'dayjs/locale/en';

// Extend dayjs with required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);

// Helper function to get locale-specific date format (with year)
export function getLocaleDateFormat(locale) {
  const baseLocale = locale ? locale.split('-')[0] : 'en';
  // Spanish uses DD/MM/YYYY, others use MM/DD/YYYY
  if (baseLocale === 'es') {
    return 'DD/MM/YYYY';
  }
  return 'MM/DD/YYYY';
}

// Helper function to get locale-specific date format (without year)
export function getLocaleDateWithoutYearFormat(locale) {
  const baseLocale = locale ? locale.split('-')[0] : 'en';
  // Spanish uses DD/MM, others use MM/DD
  if (baseLocale === 'es') {
    return 'DD/MM';
  }
  return 'MM/DD';
}

// Helper function to get locale-specific time format
export function getLocaleTimeFormat(locale) {
  const baseLocale = locale ? locale.split('-')[0] : 'en';
  // Most European locales use 24h format, US uses 12h
  if (baseLocale === 'en') {
    return 'h:mm A'; // 12-hour format with AM/PM
  }
  return 'HH:mm'; // 24-hour format
}

// Helper function to get locale-specific date+time format
export function getLocaleDateTimeFormat(locale) {
  const dateFormat = getLocaleDateFormat(locale);
  const timeFormat = getLocaleTimeFormat(locale);
  return `${dateFormat} ${timeFormat}`;
}

// Helper function to format date with locale-aware format tokens
export function formatWithLocale(date, format, locale) {
  const baseLocale = locale ? locale.split('-')[0] : 'en';
  
  // Set locale globally for this format operation
  const previousLocale = dayjs.locale();
  try {
    dayjs.locale(baseLocale);
  } catch (e) {
    // If locale not available, use 'en'
    dayjs.locale('en');
  }
  
  // Handle locale-specific format tokens
  let formatted;
  if (format === 'L') {
    // L format: date without year (day/month only) - e.g., MM/DD or DD/MM
    formatted = date.format(getLocaleDateWithoutYearFormat(locale));
  } else if (format === 'LL') {
    // LL format: long date format with year - e.g., "D MMMM YYYY"
    formatted = date.format('D MMMM YYYY');
  } else if (format === 'LLL') {
    // LLL format: date with year (full date) - e.g., MM/DD/YYYY or DD/MM/YYYY
    formatted = date.format(getLocaleDateFormat(locale));
  } else if (format === 'LT') {
    formatted = date.format(getLocaleTimeFormat(locale));
  } else if (format === 'LTS') {
    // Time with seconds - add seconds to the time format
    const timeFormat = getLocaleTimeFormat(locale);
    // Replace :mm with :mm:ss (works for both HH:mm and h:mm A formats)
    formatted = date.format(timeFormat.replace(/:mm/, ':mm:ss'));
  } else {
    // Use format as-is for other formats
    formatted = date.format(format);
  }
  
  // Restore previous locale
  try {
    dayjs.locale(previousLocale);
  } catch (e) {
    dayjs.locale('en');
  }
  
  return formatted;
}

export default dayjs;

